import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Chart from 'react-apexcharts';
import { Card, Col, Dropdown, Spinner, Alert } from 'react-bootstrap';
import CustomToggle from './dropdowns';
import { fetchAllUserCount } from '../store/chart/chartThunk';

const ChartComponent = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rawData, setRawData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('all');

    // Memoized chart options for better performance
    const chartOptions = useMemo(() => ({
        colors: ["#50B5FF", "#28a745", "#ffc107"],
        chart: {
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: false,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: true
                }
            },
            type: 'line',
            height: 350,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                }
            }
        },
        forecastDataPoints: {
            count: 2,
            fillOpacity: 0.5,
            strokeWidth: undefined,
            dashArray: 4,
        },
        stroke: {
            width: 3,
            curve: 'smooth'
        },
        grid: {
            show: true,
            strokeDashArray: 5,
            borderColor: '#e7e7e7',
            row: {
                colors: ['transparent', 'transparent'],
                opacity: 0.5
            },
            column: {
                colors: ['transparent'],
                opacity: 0.5
            }
        },
        markers: {
            size: 6,
            colors: "#FFFFFF",
            strokeColors: ["#50B5FF"],
            strokeWidth: 2,
            strokeOpacity: 0.9,
            strokeDashArray: 0,
            fillOpacity: 1,
            shape: "circle",
            radius: 2,
            offsetX: 0,
            offsetY: 0,
            hover: {
                size: 8,
                sizeOffset: 3
            }
        },
        xaxis: {
            type: 'category',
            categories: rawData?.labels || [],
            labels: {
                style: {
                    colors: '#7E8B9A',
                    fontSize: '12px'
                },
                rotate: -45,
                rotateAlways: false
            },
            axisBorder: {
                show: true,
                color: '#e7e7e7'
            },
            axisTicks: {
                show: true,
                color: '#e7e7e7'
            },
            tooltip: {
                enabled: true,
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#7E8B9A',
                    fontSize: '12px'
                },
                formatter: function (value) {
                    return Math.floor(value).toLocaleString();
                }
            },
            title: {
                text: 'Number of Users',
                style: {
                    color: '#7E8B9A',
                    fontSize: '12px'
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            theme: 'light',
            style: {
                fontSize: '12px'
            },
            x: {
                format: 'dd MMM yyyy'
            },
            y: {
                formatter: function (value, { series, seriesIndex, dataPointIndex, w }) {
                    return value.toLocaleString() + ' users';
                }
            }
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            floating: false,
            fontSize: '12px',
            fontFamily: 'inherit',
            fontWeight: 400,
            formatter: undefined,
            inverseOrder: false,
            width: undefined,
            height: undefined,
            tooltipHoverFormatter: undefined,
            customLegendItems: [],
            offsetX: 0,
            offsetY: 0,
            labels: {
                colors: '#7E8B9A',
                useSeriesColors: false
            },
            markers: {
                width: 12,
                height: 12,
                strokeWidth: 0,
                strokeColor: '#fff',
                fillColors: undefined,
                radius: 12,
                customHTML: undefined,
                onClick: undefined,
                offsetX: 0,
                offsetY: 0
            },
            itemMargin: {
                horizontal: 5,
                vertical: 0
            }
        }
    }), [rawData]);

    // Process chart series data
    const chartSeries = useMemo(() => {
        if (!rawData || !rawData.series || !Array.isArray(rawData.series)) {
            return [{
                name: "Total Users",
                data: [],
            }];
        }

        return rawData.series.map((serie, index) => ({
            name: serie.name || `Series ${index + 1}`,
            data: Array.isArray(serie.data) ? serie.data : [],
        }));
    }, [rawData, selectedPeriod]);

    // Fetch data effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await dispatch(fetchAllUserCount());
                console.log('API Response:', response);
                
                const data = response.payload || response;
                
                // Validate data structure
                if (!data) {
                    throw new Error('No data received from API');
                }

                if (!data.labels || !Array.isArray(data.labels) || data.labels.length === 0) {
                    throw new Error('Invalid or empty labels data');
                }

                if (!data.series || !Array.isArray(data.series) || data.series.length === 0) {
                    throw new Error('Invalid or empty series data');
                }

                // Validate series data structure
                const validSeries = data.series.every(serie => 
                    serie && 
                    Array.isArray(serie.data) && 
                    serie.data.length === data.labels.length
                );

                if (!validSeries) {
                    throw new Error('Series data length mismatch with labels');
                }

                setRawData(data);
                console.log('Chart data processed successfully:', data);

            } catch (error) {
                console.error('Error fetching chart data:', error);
                setError(error.message || 'Failed to fetch chart data');
                setRawData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dispatch]);

    // Period filter options
    const periodOptions = [
        { key: 'all', label: 'All Time' },
        { key: 'last30', label: 'Last 30 Days' },
        { key: 'last7', label: 'Last 7 Days' },
        { key: 'today', label: 'Today' }
    ];

    // Handle period change
    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (!rawData || !rawData.series || rawData.series.length === 0) {
            return { total: 0, growth: 0, average: 0 };
        }

        const mainSeries = rawData.series[0];
        if (!mainSeries || !Array.isArray(mainSeries.data)) {
            return { total: 0, growth: 0, average: 0 };
        }

        const data = mainSeries.data;
        const total = data[data.length - 1] || 0;
        const previous = data[data.length - 2] || 0;
        const growth = previous ? ((total - previous) / previous * 100) : 0;
        const average = data.length ? data.reduce((sum, val) => sum + val, 0) / data.length : 0;

        return { total, growth, average };
    }, [rawData]);

    // Loading state
    if (loading) {
        return (
            <Col lg="12">
                <Card className="card-block card-stretch card-height">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <h4 className="card-title mb-0">User Analytics</h4>
                        </div>
                    </Card.Header>
                    <Card.Body className="pt-0">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                            <div className="text-center">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading chart data...</p>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        );
    }

    // Error state
    if (error) {
        return (
            <Col lg="12">
                <Card className="card-block card-stretch card-height">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <h4 className="card-title mb-0">User Analytics</h4>
                        </div>
                    </Card.Header>
                    <Card.Body className="pt-0">
                        <Alert variant="danger" className="text-center">
                            <Alert.Heading>Error Loading Data</Alert.Heading>
                            <p>{error}</p>
                            <hr />
                            <div className="d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={() => window.location.reload()}
                                >
                                    <i className="fas fa-redo me-2"></i>
                                    Retry
                                </button>
                            </div>
                        </Alert>
                    </Card.Body>
                </Card>
            </Col>
        );
    }

    // No data state
    if (!rawData || !rawData.labels || rawData.labels.length === 0) {
        return (
            <Col lg="12">
                <Card className="card-block card-stretch card-height">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <h4 className="card-title mb-0">User Analytics</h4>
                        </div>
                    </Card.Header>
                    <Card.Body className="pt-0">
                        <div className="text-center" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div>
                                <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                <h5 className="text-muted">No Data Available</h5>
                                <p className="text-muted">There's no data to display at the moment.</p>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        );
    }

    // Main chart component
    return (
        <Col lg="12">
            <Card className="card-block card-stretch card-height">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h4 className="card-title mb-2">User Analytics</h4>
                            <p className="text-muted mb-0">Track user growth over time</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            {/* Summary Stats */}
                            <div className="d-flex gap-3">
                                <div className="text-center">
                                    <h6 className="mb-0 text-primary">{summaryStats?.total?.toLocaleString()}</h6>
                                    <small className="text-muted">Total Users</small>
                                </div>
                                <div className="text-center">
                                    <h6 className={`mb-0 ${summaryStats?.growth >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {summaryStats?.growth >= 0 ? '+' : ''}{summaryStats?.growth?.toFixed(1)}%
                                    </h6>
                                    <small className="text-muted">Growth</small>
                                </div>
                            </div>
                            
                            {/* Period Selector */}
                            <Dropdown>
                                <Dropdown.Toggle as={CustomToggle} id="period-dropdown">
                                    <i className="fas fa-calendar me-2"></i>
                                    {periodOptions.find(opt => opt.key === selectedPeriod)?.label}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {periodOptions.map((option) => (
                                        <Dropdown.Item
                                            key={option.key}
                                            active={selectedPeriod === option.key}
                                            onClick={() => handlePeriodChange(option.key)}
                                        >
                                            {option.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="pt-0">
                    <div className="admin-chart-02">
                        <Chart
                            options={chartOptions}
                            series={chartSeries}
                            type="line"
                            height="400"
                        />
                    </div>
                    
                    {/* Additional Info */}
                    <div className="mt-3 pt-3 border-top">
                        <div className="row text-center">
                            <div className="col-md-4">
                                <h6 className="text-muted mb-1">Average</h6>
                                <span className="h5">{Math.round(summaryStats.average).toLocaleString()}</span>
                            </div>
                            <div className="col-md-4">
                                <h6 className="text-muted mb-1">Data Points</h6>
                                <span className="h5">{rawData.labels.length}</span>
                            </div>
                            <div className="col-md-4">
                                <h6 className="text-muted mb-1">Last Updated</h6>
                                <span className="h6">{new Date().toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
};

export default ChartComponent;